import { Subscriptions } from "@bezoned/shared/features/subscriptions/subscriptions.constants.js";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Tooltip,
} from "@fluentui/react-components";
import { ArrowSyncRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useRef, useState } from "react";

import { uploadTeamLogoToBlob } from "../../../services/api.service.js";
import { useSubscription } from "../../subscription/subscription.context.js";
import { TeamCreateEnterpriseGeneralTab } from "./team-create-enterprise-general-tab.js";
import type { ThemeDraft } from "./team-create-flow.js";
import { TeamCreateThemeTab } from "./team-create-theme-tab.js";
import type { useCreateTeamDialog } from "./use-create-team-dialog.js";

interface EnterpriseState {
  step: "General" | "Theme";
  teamId: string;
  theme: ThemeDraft;
  error: string | null;
  logoError: string | null;
}

const initialState = (): EnterpriseState => ({
  error: null,
  logoError: null,
  step: "General",
  teamId: "",
  theme: {
    selectedLogoFile: null,
    selectedLogoPreviewUrl: null,
    selectedPresetId: null,
  },
});

export const TeamCreateEnterpriseDialog = (
  props: ReturnType<typeof useCreateTeamDialog>["props"]
) => {
  const { planId } = useSubscription();
  const isStarter = planId === Subscriptions.StarterPlan;
  const [state, setState] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [retryValidation, setRetryValidation] = useState<(() => void) | null>(
    null
  );
  const submitInFlightRef = useRef(false);
  const handleRetryValidationChange = useCallback(
    (retry: (() => void) | null) => setRetryValidation(() => retry),
    []
  );

  useEffect(() => {
    if (!props.open) {
      return;
    }

    setState(initialState());
    setSubmitting(false);
    setCanContinue(false);
    setRetryValidation(null);
    submitInFlightRef.current = false;
  }, [props.open]);

  const updateTheme = (draft: Partial<ThemeDraft>) =>
    setState((current) => ({
      ...current,
      theme: { ...current.theme, ...draft },
    }));

  const submit = async (data: {
    presetId?: string;
    teamId: string;
    uploadToken: string | null;
  }) => {
    if (submitInFlightRef.current || props.loading) {
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);

    try {
      const { uploadToken: submittedUploadToken } = data;
      let uploadToken = submittedUploadToken;
      if (!uploadToken && state.theme.selectedLogoFile) {
        uploadToken = await uploadTeamLogoToBlob(state.theme.selectedLogoFile);
      }

      const result = await props.handleCreateTeam({
        msTeamId: data.teamId,
        type: "enterprise",
        ...(data.presetId ? { presetId: data.presetId } : {}),
        ...(uploadToken ? { uploadToken } : {}),
      });

      if (!result.ok) {
        setState((current) => ({
          ...current,
          error: result.error.message,
        }));
      }
    } catch {
      setState((current) => ({
        ...current,
        logoError: "Failed to create office. Please try again.",
      }));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      onOpenChange={(_, data) => {
        if (!data.open) {
          props.setOpen(false);
        }
      }}
      open={props.open}
    >
      <DialogSurface className="relative sm:max-h-[90lvh] sm:w-[90lvw] md:min-w-[900px] lg:min-w-[1000px]">
        <DialogBody className="flex flex-col sm:max-h-[90lvh]">
          <DialogTitle className="text-bzd-gray-400">
            Create new office
          </DialogTitle>
          <DialogContent className="flex flex-1 flex-col overflow-hidden px-6 pb-6">
            {state.step === "General" ? (
              <TeamCreateEnterpriseGeneralTab
                key={props.open ? "open" : "closed"}
                disabled={submitting || props.loading}
                errorMessage={state.error}
                teamId={state.teamId}
                logoErrorMessage={state.logoError}
                logoPreviewUrl={state.theme.selectedLogoPreviewUrl}
                onDeleteLogo={() =>
                  updateTheme({
                    selectedLogoFile: null,
                    selectedLogoPreviewUrl: null,
                  })
                }
                onLogoErrorMessageChange={(logoError) =>
                  setState((current) => ({ ...current, logoError }))
                }
                onLogoPreviewUrlChange={(url) =>
                  updateTheme({ selectedLogoPreviewUrl: url })
                }
                onSelectedLogoFileChange={(file) =>
                  updateTheme({ selectedLogoFile: file })
                }
                onSubmit={async (teamId) => {
                  if (isStarter) {
                    await submit({ teamId, uploadToken: null });
                    return;
                  }

                  setState((current) => ({
                    ...current,
                    step: "Theme",
                    teamId,
                  }));
                }}
                onCanContinueChange={setCanContinue}
                onRetryValidationChange={handleRetryValidationChange}
                onTeamIdChange={(teamId) =>
                  setState((current) => ({ ...current, error: null, teamId }))
                }
              />
            ) : null}

            {state.step === "Theme" ? (
              <TeamCreateThemeTab
                onSubmit={({ presetId, uploadToken }) =>
                  submit({ presetId, teamId: state.teamId, uploadToken })
                }
                setThemeDraft={updateTheme}
                submitting={submitting || props.loading}
                themeDraft={state.theme}
              />
            ) : null}
          </DialogContent>
          {state.step === "General" ? (
            <DialogActions className="mt-2 flex w-full justify-end gap-2">
              <Button
                appearance="primary"
                disabled={submitting || props.loading || !canContinue}
                form="enterprise-team-form"
                type="submit"
              >
                {isStarter ? "Create office" : "Continue"}
              </Button>
              {retryValidation ? (
                <Tooltip content="Reload Team validation" relationship="label">
                  <Button
                    appearance="subtle"
                    aria-label="Reload Team validation"
                    disabled={submitting || props.loading}
                    icon={<ArrowSyncRegular />}
                    onClick={retryValidation}
                    type="button"
                  />
                </Tooltip>
              ) : null}
            </DialogActions>
          ) : null}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
